import React, { useState, useEffect } from 'react';

const NodeForm = ({ node, onSave, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false); // ADD THIS

  // Initialize form data when node changes
  useEffect(() => {
    if (node) {
      console.log('NodeForm initializing with node:', node);
      setFormData({
        id: node.id || null,
        name: node.name || '',
        order_index: node.order_index || 1,
        node_type: node.node_type || 'topic',
        parent_id: node.parent_id || null,
        is_new: node.is_new || false,
        // Type-specific fields
        description: node.description || '',
        text_en: node.text_en || '',
        text_hi: node.text_hi || '',
        content_en: node.content_en || '',
        content_hi: node.content_hi || '',
        meta_tag: node.meta_tag || 'explanation',
        image_path: node.image_path || '',
        // Parent IDs
        subject_id: node.subject_id || null,
        chapter_id: node.chapter_id || null,
        topic_id: node.topic_id || null,
        subtopic_id: node.subtopic_id || null,
        chunk_id: node.chunk_id || null,
        micro_chunk_id: node.micro_chunk_id || null,
      });
      setErrors({});
    }
  }, [node]);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Name validation only for topics and subtopics
    if ((formData.node_type === 'topic' || formData.node_type === 'subtopic') && !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.order_index < 1) {
      newErrors.order_index = 'Order index must be at least 1';
    }

    // Type-specific validation
    if (formData.node_type === 'chunk' && !formData.text_en.trim()) {
      newErrors.text_en = 'English content is required for chunks';
    }

    if (formData.node_type === 'micro_chunk' && !formData.text_en.trim()) {
      newErrors.text_en = 'English content is required for micro chunks';
    }

    if (formData.node_type === 'explanation' && !formData.content_en.trim()) {
      newErrors.content_en = 'English content is required for explanations';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);

    if (validateForm()) {
      setIsSaving(true); // START loading
      try {
      // Transform data for backend based on node type
      const saveData = { ...formData };
      
      // Map fields correctly for different node types
      if (formData.node_type === 'chunk') {
        // For chunks, backend expects chunk_text (not text_en) and no name
        saveData.chunk_text = formData.text_en;
        saveData.chunk_text_hi = formData.text_hi;
        // Remove the incorrect field names
        delete saveData.text_en;
        delete saveData.text_hi;
        delete saveData.name; // Chunks don't have names
      } else if (formData.node_type === 'micro_chunk') {
        // For micro chunks, backend expects content (not text_en) and no name
        saveData.content = formData.text_en;
        saveData.content_hi = formData.text_hi;
        // Remove the incorrect field names
        delete saveData.text_en;
        delete saveData.text_hi;
        delete saveData.name; // Micro chunks don't have names
      } else if (formData.node_type === 'explanation') {
        // Explanations don't have names
        delete saveData.name;
      }
      
      console.log('🚀 Transformed data for backend:', saveData);
      await onSave(saveData);
      } finally {
        setIsSaving(false); // STOP loading (even if error)
      //onSave(saveData);
    }
     }
  };

  // Handle "Save & Add Another" - saves current and opens new form with same parent
  const handleSaveAndAddAnother = async (e) => {
    e.preventDefault();
    console.log('Save & Add Another clicked');

    if (validateForm()) {
      setIsSaving(true);
      
      try {
        // Transform data for backend
        const saveData = { ...formData };
        
        // Apply same transformations as regular save
        if (formData.node_type === 'chunk') {
          saveData.chunk_text = formData.text_en;
          saveData.chunk_text_hi = formData.text_hi;
          delete saveData.text_en;
          delete saveData.text_hi;
          delete saveData.name;
        } else if (formData.node_type === 'micro_chunk') {
          saveData.content = formData.text_en;
          saveData.content_hi = formData.text_hi;
          delete saveData.text_en;
          delete saveData.text_hi;
          delete saveData.name;
        } else if (formData.node_type === 'explanation') {
          delete saveData.name;
        }
        
        console.log('🚀 Save & Add Another - data:', saveData);
        
        // Save the current node
        await onSave(saveData, true); // Pass 'true' flag to indicate "add another"
        
        // Note: Parent component will handle opening new form with same context
        
      } finally {
        setIsSaving(false);
      }
    }
  };


  // Get node type display name
  const getNodeTypeDisplay = (nodeType) => {
    switch (nodeType) {
      case 'topic': return 'Topic';
      case 'subtopic': return 'Subtopic';
      case 'chunk': return 'Chunk';
      case 'micro_chunk': return 'Micro Chunk';
      case 'explanation': return 'Explanation';
      default: return nodeType;
    }
  };

  // Render form field with error handling
  const renderField = (field, label, type = 'text', required = false, options = {}) => {
    const value = formData[field] || '';
    const error = errors[field];

    return (
      <div className="form-field">
        <label className={`form-label ${required ? 'required' : ''}`}>
          {label}
        </label>
        {type === 'textarea' ? (
          <textarea
            className={`form-textarea ${error ? 'error' : ''}`}
            value={value}
            onChange={(e) => handleChange(field, e.target.value)}
            placeholder={options.placeholder || ''}
            rows={options.rows || 4}
          />
        ) : type === 'select' ? (
          <select
            className={`form-select ${error ? 'error' : ''}`}
            value={value}
            onChange={(e) => handleChange(field, e.target.value)}
          >
            {options.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            className={`form-input ${error ? 'error' : ''}`}
            value={value}
            onChange={(e) => handleChange(field, type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
            placeholder={options.placeholder || ''}
          />
        )}
        {error && <div className="form-error">{error}</div>}
      </div>
    );
  };

  if (!node) {
    return <div>No node data available</div>;
  }

  return (
    <div className="node-form">
      <div className="form-header">
        <h3>
          {formData.is_new ? '➕ Creating' : '✏️ Editing'} {getNodeTypeDisplay(formData.node_type)}
        </h3>
        <div className="form-actions">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onCancel} 
            disabled={isSaving}
          >
            Cancel
          </button>
          
          {/* Show "Save & Add Another" only when creating new nodes */}
          {formData.is_new && (
            <button 
              type="button"
              className="btn btn-success"
              onClick={handleSaveAndAddAnother}
              disabled={isSaving}
            >
              {isSaving ? '⏳ Saving...' : '➕ Save & Add Another'}
            </button>
          )}
          
          <button 
            type="submit" 
            form="node-form" 
            className="btn btn-primary"
            disabled={isSaving}
          >
            {isSaving ? '⏳ Saving...' : (formData.is_new ? '✓ Create' : '✓ Update')}
          </button>
        </div>
      </div>

      <form id="node-form" onSubmit={handleSubmit} className="form-content">
        {/* Basic Information */}
        <div className="form-section">
          
          
          {/* Name field only for topics and subtopics */}
          {(formData.node_type === 'topic' || formData.node_type === 'subtopic') && (
            renderField('name', 'Name', 'text', true, { 
              placeholder: `Enter ${formData.node_type} name` 
            })
          )}

          {renderField('order_index', 'Order Index', 'number', true)}

          {/* Description for topics and subtopics */}
          {(formData.node_type === 'topic' || formData.node_type === 'subtopic') && (
            renderField('description', 'Description', 'textarea', false, {
              placeholder: 'Enter description (optional)',
              rows: 3
            })
          )}
        </div>

        {/* Content Section for chunks and micro chunks */}
        {(formData.node_type === 'chunk' || formData.node_type === 'micro_chunk') && (
          <div className="form-section">
            <h4>Content</h4>
            <div className="bilingual-fields">
              <div className="field-group">
                {renderField('text_en', 'English Content', 'textarea', true, {
                  placeholder: 'Enter content in English',
                  rows: 6
                })}
              </div>
              <div className="field-group">
                {renderField('text_hi', 'Hindi Content', 'textarea', false, {
                  placeholder: 'Enter content in Hindi',
                  rows: 6
                })}
              </div>
            </div>
          </div>
        )}

        {/* Content Section for explanations */}
        {formData.node_type === 'explanation' && (
          <div className="form-section">
            <h4>Explanation Content</h4>
            
            {renderField('meta_tag', 'Type', 'select', false, {
              options: [
                { value: 'explanation', label: 'Explanation' },
                { value: 'example', label: 'Example' },
                { value: 'definition', label: 'Definition' },
                { value: 'formula', label: 'Formula' },
                { value: 'tip', label: 'Tip' }
              ]
            })}

            <div className="bilingual-fields">
              <div className="field-group">
                {renderField('content_en', 'English Content', 'textarea', true, {
                  placeholder: 'Enter explanation in English',
                  rows: 6
                })}
              </div>
              <div className="field-group">
                {renderField('content_hi', 'Hindi Content', 'textarea', false, {
                  placeholder: 'Enter explanation in Hindi',
                  rows: 6
                })}
              </div>
            </div>
          </div>
        )}

        {/* Image Section */}
        {(formData.node_type === 'chunk' || formData.node_type === 'micro_chunk' || formData.node_type === 'explanation') && (
          <div className="form-section">
            <h4>Image (Optional)</h4>
            {renderField('image_path', 'Image Path', 'text', false, {
              placeholder: 'Enter image file path or URL'
            })}
          </div>
        )}

        {/* Debug Section (can be removed in production) */}
        <div className="form-section debug-section">
          <h4>Debug Info</h4>
          <div className="debug-info">
            <div>Node Type: {formData.node_type}</div>
            <div>Is New: {formData.is_new ? 'Yes' : 'No'}</div>
            <div>Parent ID: {formData.parent_id}</div>
            <div>Subject ID: {formData.subject_id}</div>
            <div>Chapter ID: {formData.chapter_id}</div>
          </div>
        </div>
      </form>

      {/* Form Footer with Tips */}
      <div className="form-footer">
        <div className="form-tips">
          <h4>💡 Tips:</h4>
          <ul>
            <li>Name should be descriptive and unique within its parent</li>
            <li>Order index determines the sequence of content</li>
            <li>English content is required, Hindi is optional</li>
            <li>Use clear, simple language for better understanding</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NodeForm;